import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Card,
  Table,
  InputNumber,
  Button,
  message,
  Tabs,
  Space,
  Modal,
  Select,
  Divider,
  Input,
} from "antd";
import { ReloadOutlined, DeleteOutlined } from "@ant-design/icons";
import axiosInstance from "../../api/axiosInstance";

const API_URL = process.env.REACT_APP_API_URL;

interface Group {
  tournamentGroupID: string;
  name: string;
  teams: Team[];
  matches: Match[];
}

interface Team {
  teamID: string;
  teamName: string;
  wins: number;
  losses: number;
  draws: number;
  points: number;
  rank?: number;
  difference?: number; // 🆕 Hiệu số tổng
  headToHeadDiff?: number; // 🆕 Hiệu số đối đầu
}

interface Match {
  tournamentGroupMatchID: string;
  team1ID?: string;
  team2ID?: string;
  team1Name: string;
  team2Name: string;
  court?: string | null; // ✅ Cho phép null / undefined
  score1?: number | null;
  score2?: number | null;
  status: boolean;
}


export interface GroupStageMatchEditorRef {
  reload: () => void;
}

const GroupStageMatchEditor = forwardRef<
  GroupStageMatchEditorRef,
  { tournamentId: string }
>(({ tournamentId }, ref) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>();

  // 🟩 Hàm tính hiệu số tổng cho từng đội trong group
  const calculateDifferences = (group: any): Record<string, number> => {
    const scores: Record<string, { scored: number; conceded: number }> = {};
    group.tournamentGroupMatches.forEach((m: any) => {
      if (m.team1ID && m.team2ID) {
        scores[m.team1ID] = scores[m.team1ID] || { scored: 0, conceded: 0 };
        scores[m.team2ID] = scores[m.team2ID] || { scored: 0, conceded: 0 };

        if (m.score1 != null && m.score2 != null) {
          scores[m.team1ID].scored += m.score1;
          scores[m.team1ID].conceded += m.score2;
          scores[m.team2ID].scored += m.score2;
          scores[m.team2ID].conceded += m.score1;
        }
      }
    });

    const differences: Record<string, number> = {};
    Object.keys(scores).forEach((teamId) => {
      const s = scores[teamId];
      differences[teamId] = s.scored - s.conceded;
    });
    return differences;
  };

  // 🟨 Hàm tính hiệu số đối đầu giữa các đội có cùng điểm & hiệu số
  const calculateHeadToHeadDiff = (group: any, teams: any[]) => {
    const result: Record<string, number> = {};

    // nhóm các đội có cùng điểm và hiệu số
    const tiedGroups = teams.reduce((acc: Record<string, string[]>, t) => {
      const key = `${t.points}_${t.difference}`;
      acc[key] = acc[key] || [];
      acc[key].push(t.teamID);
      return acc;
    }, {});

    Object.values(tiedGroups).forEach((teamIds) => {
      if (teamIds.length < 2) return; // chỉ xét nếu có >=2 đội bằng điểm & hiệu số

      const stats: Record<string, { scored: number; conceded: number }> = {};
      group.tournamentGroupMatches.forEach((m: any) => {
        if (
          m.team1ID &&
          m.team2ID &&
          teamIds.includes(m.team1ID) &&
          teamIds.includes(m.team2ID)
        ) {
          stats[m.team1ID] = stats[m.team1ID] || { scored: 0, conceded: 0 };
          stats[m.team2ID] = stats[m.team2ID] || { scored: 0, conceded: 0 };

          if (m.score1 != null && m.score2 != null) {
            stats[m.team1ID].scored += m.score1;
            stats[m.team1ID].conceded += m.score2;
            stats[m.team2ID].scored += m.score2;
            stats[m.team2ID].conceded += m.score1;
          }
        }
      });

      teamIds.forEach((id) => {
        const s = stats[id];
        result[id] = s ? s.scored - s.conceded : 0;
      });
    });

    return result;
  };

  // 🟩 Fetch dữ liệu các bảng
  const fetchGroups = async () => {
    if (!tournamentId) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL}/v1/tournament-group/get-tournament-group-by-tournamentID/${tournamentId}`
      );

      const mapped: Group[] = res.data
        .map((g: any) => {
          const teamMap = Object.fromEntries(
            g.tournamentGroupTeams.map((t: any) => [t.teamID, t.teamName])
          );

          const differences = calculateDifferences(g);

          // map teams
          const teams = g.tournamentGroupTeams.map((t: any) => ({
            teamID: t.teamID,
            teamName: t.teamName,
            wins: t.wins ?? 0,
            losses: t.losses ?? 0,
            draws: t.draws ?? 0,
            points: t.points ?? 0,
            difference: differences[t.teamID] ?? 0,
          }));

          // 🟨 Tính hiệu số đối đầu (head-to-head)
          const headToHeadDiffs = calculateHeadToHeadDiff(g, teams);

          // 🟩 Sắp xếp thứ hạng
          teams.sort((a: any, b: any) => {
            if (b.points !== a.points) return b.points - a.points; // điểm
            if ((b.difference ?? 0) !== (a.difference ?? 0))
              return (b.difference ?? 0) - (a.difference ?? 0); // hiệu số
            if ((headToHeadDiffs[b.teamID] ?? 0) !== (headToHeadDiffs[a.teamID] ?? 0))
              return (headToHeadDiffs[b.teamID] ?? 0) - (headToHeadDiffs[a.teamID] ?? 0); // đối đầu
            return a.teamName.localeCompare(b.teamName, "vi"); // fallback
          });

          teams.forEach((t: any, i: any) => {
            t.rank = i + 1;
            t.headToHeadDiff = headToHeadDiffs[t.teamID] ?? 0;
          });

          return {
            tournamentGroupID: g.tournamentGroupID,
            name: g.name,
            teams: teams,
            matches: g.tournamentGroupMatches.map((m: any) => ({
              tournamentGroupMatchID: m.tournamentGroupMatchID,
              team1ID: m.team1ID,
              team2ID: m.team2ID,
              court: m.court,
              team1Name: teamMap[m.team1ID] || "Đội 1",
              team2Name: teamMap[m.team2ID] || "Đội 2",
              score1: m.score1 ?? null,
              score2: m.score2 ?? null,
              status: m.status ?? false,
            })),
          };
        })
        .sort((a: any, b: any) =>
          a.name.localeCompare(b.name, "vi", { numeric: true })
        );

      setGroups(mapped);
      if (!selectedGroupId && mapped.length > 0) {
        setSelectedGroupId(mapped[0].tournamentGroupID);
      }
    } catch (err) {
      console.error(err);
      message.error("Không thể tải dữ liệu vòng bảng!");
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    reload: fetchGroups,
  }));

  useEffect(() => {
    fetchGroups();
  }, [tournamentId]);

  // 🟧 Lưu điểm cho 1 trận
  const updateScore = async (match: Match, groupId: string) => {
    if (match.score1 == null || match.score2 == null) {
      message.warning("Vui lòng nhập đầy đủ điểm!");
      return;
    }
    console.log(match);

    try {
      await axiosInstance.put(
        `${API_URL}/v1/tournament-group/update-tournament-group-match/${match.tournamentGroupMatchID}`,
        {
          tournamentGroupMatchID: match.tournamentGroupMatchID,
          court: match.court,
          score1: match.score1,
          score2: match.score2,
        }
      );

      message.success("Cập nhật điểm thành công!");
      await fetchGroups();
      setSelectedGroupId(groupId);
    } catch (err) {
      console.error(err);
      message.error("Lỗi khi cập nhật điểm!");
    }
  };

  // 🟥 Xóa bảng
  const deleteTournamentGroup = async () => {
    if (!selectedGroupId) {
      message.warning("Vui lòng chọn bảng đấu cần xóa!");
      return;
    }

    Modal.confirm({
      title: "Xác nhận xóa bảng đấu",
      content:
        "Bạn có chắc chắn muốn xóa bảng này? Tất cả trận đấu và đội trong bảng sẽ bị xóa!",
      okText: "Xóa",
      cancelText: "Hủy",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosInstance.put(
            `${API_URL}/v1/tournament-group/delete-tournament-group`,
            { tournamentGroupIDs: [selectedGroupId] }
          );
          message.success("Đã xóa bảng đấu!");
          setSelectedGroupId(undefined);
          fetchGroups();
        } catch (err) {
          console.error(err);
          message.error("Không thể xóa bảng đấu!");
        }
      },
    });
  };

  // 🟦 Cập nhật điểm trong UI tạm thời
  const handleScoreChange = (
    groupId: string,
    matchId: string,
    field: "score1" | "score2",
    value: number | null
  ) => {
    setGroups((prev) =>
      prev.map((gr) =>
        gr.tournamentGroupID === groupId
          ? {
            ...gr,
            matches: gr.matches.map((m) =>
              m.tournamentGroupMatchID === matchId
                ? { ...m, [field]: value }
                : m
            ),
          }
          : gr
      )
    );
  };
  const handleCourtChange = (
    groupId: string,
    matchId: string,
    value: string | null
  ) => {
    setGroups((prev) =>
      prev.map((gr) =>
        gr.tournamentGroupID === groupId
          ? {
            ...gr,
            matches: gr.matches.map((m) =>
              m.tournamentGroupMatchID === matchId
                ? { ...m, court: value ?? null }
                : m
            ),
          }
          : gr
      )
    );
  };

  return (
    <Tabs
      type="card"
      activeKey={selectedGroupId}
      onChange={(key) => setSelectedGroupId(key)}
      tabBarExtraContent={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchGroups}
            loading={loading}
          >
            Làm mới
          </Button>
          <Select
            placeholder="Chọn bảng cần xóa"
            style={{ width: 200 }}
            value={selectedGroupId}
            onChange={setSelectedGroupId}
            options={groups.map((g) => ({
              label: g.name,
              value: g.tournamentGroupID,
            }))}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={deleteTournamentGroup}
            disabled={!selectedGroupId}
          >
            Xóa bảng
          </Button>
        </Space>
      }
      items={groups.map((g) => ({
        key: g.tournamentGroupID,
        label: g.name,
        children: (
          <Card size="small" bordered>
            <h4>Danh sách đội trong {g.name}</h4>
            <Table
              loading={loading}
              dataSource={g.teams}
              rowKey="teamID"
              pagination={false}
              size="small"
              columns={[
                { title: "STT", render: (_, __, i) => i + 1 },
                { title: "Tên đội", dataIndex: "teamName" },
                { title: "Thắng", dataIndex: "wins" },
                { title: "Thua", dataIndex: "losses" },
                { title: "Điểm", dataIndex: "points" },
                { title: "Hiệu số", dataIndex: "difference" },
                { title: "Đối đầu", dataIndex: "headToHeadDiff" },
                { title: "Hạng", dataIndex: "rank" },
              ]}
            />
            <Divider />
            <h4>Danh sách trận đấu</h4>
            <Table
              loading={loading}
              dataSource={g.matches}
              rowKey="tournamentGroupMatchID"
              pagination={false}
              size="small"
              columns={[
                { title: "Trận", render: (_, __, i) => i + 1 },
                { title: "Đội 1", dataIndex: "team1Name" },
                {
                  title: "Điểm đội 1",
                  render: (_, record) => (
                    <InputNumber
                      min={0}
                      value={record.score1 ?? undefined}
                      onChange={(v) =>
                        handleScoreChange(
                          g.tournamentGroupID,
                          record.tournamentGroupMatchID,
                          "score1",
                          v ?? null
                        )
                      }
                    />
                  ),
                },
                { title: "Đội 2", dataIndex: "team2Name" },
                {
                  title: "Điểm đội 2",
                  render: (_, record) => (
                    <InputNumber
                      min={0}
                      value={record.score2 ?? undefined}
                      onChange={(v) =>
                        handleScoreChange(
                          g.tournamentGroupID,
                          record.tournamentGroupMatchID,
                          "score2",
                          v ?? null
                        )
                      }
                    />
                  ),
                },
                {
                  title: "Sân thi đấu",
                  render: (_, record) => (
                    <Input
                      value={record.court ?? ""}
                      placeholder="Nhập tên sân"
                      onChange={(e) =>
                        handleCourtChange(
                          g.tournamentGroupID,
                          record.tournamentGroupMatchID,
                          e.target.value
                        )
                      }
                    />
                  ),
                },

                {
                  title: "Thao tác",
                  render: (_, record) => (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => updateScore(record, g.tournamentGroupID)}
                    >
                      Lưu
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        ),
      }))}
    />
  );
});

GroupStageMatchEditor.displayName = "GroupStageMatchEditor";
export default GroupStageMatchEditor;
